import React from 'react';
import { Box, Typography } from '@mui/material';
import { Panel, ResultTable, SelectMenu, type ResultColumn } from '../../../../ui-kit';
import type { ReportCase, ReportPayload, ReportTerm } from '../../../../types/reportDashboard';
import { formatReportValue, money, shortDate } from '../reportFormat';
import { termHint } from '../termHint';
import { useReportDates } from '../useReportDates';
import type { ReportQuery } from '../useReportCohort';
import type { ReportDownload } from '../useReportDownload';

export interface CaseListDef {
  /** The payload key, which is also what the download asks for. */
  block: 'pending_uw' | 'pending_carrier' | 'issued' | 'approved_as_is' | 'approved_better' | 'approved_worse' | 'declined' | 'closed_lost' | 'closing_out';
  /** The term this list counts; its info icon sits on the panel title. */
  listTerm: string;
  /** Names the days column: days aged in UW, days since issued. */
  daysTerm: string;
  /** Names the received column; left out when the list has none. */
  receivedTerm?: string;
  /** A decided list: shows the decision and its date. */
  showDecision?: boolean;
  /** The declined list: shows why underwriting declined. */
  showReason?: boolean;
  /** The issued list: shows when the policy was delivered. */
  showDelivered?: boolean;
  /**
   * Carrier up front, before the days. On the lists where the carrier is the
   * reason — it sets the day a case closes out — it belongs beside the days,
   * not away among the other columns.
   */
  carrierFirst?: boolean;
}

interface CaseListPanelProps {
  /**
   * One list, or several with the same columns told apart by status, e.g.
   * Pending (in UW) and Pending (carrier). Several get a Status select.
   */
  lists: CaseListDef[];
  payload: ReportPayload;
  query: ReportQuery;
  /** The panel's name. Without one, a single list is named by its own term. */
  title?: string;
  subtitle: string;
  download: ReportDownload;
  loading?: boolean;
}

type Cell = string | number | null;

// A count with its premium beside it, as every count on this report is: "366 · $1.36M".
const withPremium = (count: number, premium: number, unit = '') =>
  `${count.toLocaleString('en-US')}${unit} · ${formatReportValue(premium, 'money')}`;

/**
 * Her aged lists — Pending (in UW), Pending (carrier), Policy Issued — oldest
 * first, sortable and paged, with the board's Excel download and full screen
 * view. Lists that share their columns share a panel, with a Status select.
 *
 * Her 0-5 / 6-15 / 16+ counts are the switch above the list: each shows its
 * count and, picked, narrows the list to those cases. Cases are named by
 * policy number and HubSpot contact ID.
 */
export default function CaseListPanel({ lists, payload, query, title: panelTitle, subtitle, download, loading }: CaseListPanelProps) {
  const [status, setStatus] = React.useState(lists[0].block);
  const [band, setBand] = React.useState('all');
  const current = lists.find((l) => l.block === status) ?? lists[0];
  const { block, listTerm, daysTerm, receivedTerm, showDecision, showReason, showDelivered, carrierFirst } = current;
  // The dates on offer belong to the status being shown, so the select changes with it.
  const dates = useReportDates(payload, query, payload[block].dates);
  const list = dates.source[block];
  const pickStatus = (next: string) => {
    setStatus(next as CaseListDef['block']);
    setBand('all');
  };
  const term = (key: string): ReportTerm | undefined => list.terms[key];
  const hinted = (key: string, label: string): Pick<ResultColumn, 'label' | 'info' | 'infoTone'> => {
    const t = term(key);
    if (!t) return { label };
    const hint = termHint(t);
    return { label: t.label, info: hint.title, infoTone: hint.tone };
  };

  const text = (v: unknown) => (v === null || v === undefined || v === '' ? '—' : String(v));
  const date = (v: unknown) => shortDate((v as string | null) ?? null);
  const dollars = (v: unknown) => money((v as number | null) ?? null);
  const count = (v: unknown) => (typeof v === 'number' ? v.toLocaleString('en-US') : '—');

  const fields: { col: ResultColumn; value: (c: ReportCase) => Cell }[] = [
    { col: { key: 'policy', label: 'Policy number', format: text }, value: (c) => c.policy_number },
    { col: { key: 'contact', label: 'HubSpot contact ID', format: text }, value: (c) => c.hubspot_contact_id },
    ...(carrierFirst
      ? [{ col: { key: 'carrier', label: 'Carrier', format: text }, value: (c: ReportCase) => c.carrier }]
      : []),
    { col: { key: 'days', ...hinted(daysTerm, 'Days aged'), numeric: true, format: count }, value: (c) => c.days_aged },
    { col: { key: 'app_date', ...hinted('application_date', 'Application date'), format: date }, value: (c) => c.application_date },
    ...(receivedTerm
      ? [{ col: { key: 'received', ...hinted(receivedTerm, 'Received date'), format: date }, value: (c: ReportCase) => c.received_date }]
      : []),
    { col: { key: 'status', label: 'App status', format: text }, value: (c) => c.status },
    // A declined case has no approval rating, so it shows the date alone.
    ...(showDecision
      ? [{ col: { key: 'decision', ...hinted('uw_decision', 'UW decision'), format: text }, value: (c: ReportCase) => c.uw_decision }]
      : []),
    ...(showDecision || showReason
      ? [{
          col: showDelivered
            ? { key: 'decision_date', ...hinted('issue_date', 'Issue date'), format: date }
            : { key: 'decision_date', label: 'Date of decision', format: date },
          value: (c: ReportCase) => c.decision_date,
        }]
      : []),
    ...(showReason
      ? [{ col: { key: 'reason', ...hinted('decline_reason', 'Decline reason'), format: text }, value: (c: ReportCase) => c.decline_reason }]
      : []),
    ...(showDelivered
      ? [{ col: { key: 'delivered', label: 'Delivered date', format: date }, value: (c: ReportCase) => c.delivered_date }]
      : []),
    // Every list carries the requirements: she asks for them on the issued
    // report too, where an amendment at issue would show up.
    {
      // Each requirement with the day it was asked for, rather than a list of
      // names and one date that only covers the latest of them.
      col: { key: 'requirement', ...hinted('requirement', 'Requirement'), format: text },
      value: (c) => (c.requirements.length
        ? c.requirements.map((q) => `${q.requirement} · ${shortDate(q.date)}`).join(', ')
        : 'None ordered'),
    },
    {
      col: { key: 'activity', ...hinted('last_activity', 'Last activity'), format: text },
      value: (c) => (c.last_activity ? `${c.last_status ?? ''} · ${shortDate(c.last_activity)}` : null),
    },
    { col: { key: 'product', label: 'Product', format: text }, value: (c) => c.product },
    ...(carrierFirst ? [] : [{ col: { key: 'carrier', label: 'Carrier', format: text }, value: (c: ReportCase) => c.carrier }]),
    { col: { key: 'agent', label: 'Agent', format: text }, value: (c) => c.agent },
    { col: { key: 'case_managers', label: 'Case manager', format: text }, value: (c) => c.case_managers ?? null },
    { col: { key: 'face', label: 'Face amount', numeric: true, format: dollars }, value: (c) => c.face_amount },
    { col: { key: 'premium', ...hinted('modal_premium', 'Annual premium'), numeric: true, format: dollars }, value: (c) => c.modal_premium },
    // An approved list totals the premium the carrier approved, so it shows it.
    ...(showDecision
      ? [{ col: { key: 'approved_premium', ...hinted('approved_premium', 'Approved premium'), numeric: true, format: dollars }, value: (c: ReportCase) => c.approved_premium }]
      : []),
    { col: { key: 'target', ...hinted('target_premium', 'Target premium'), numeric: true, format: dollars }, value: (c) => c.target_premium },
    { col: { key: 'excess', ...hinted('excess_premium', 'Excess premium'), numeric: true, format: dollars }, value: (c) => c.excess_premium },
    { col: { key: 'channel', label: 'Channel', format: text }, value: (c) => c.channel },
    { col: { key: 'campaign', label: 'Campaign/program', format: text }, value: (c) => c.campaign },
  ];
  const columns = fields.map((f) => f.col);

  const picked = list.aging.find((a) => a.key === band);
  const rows = React.useMemo(
    () => list.cases
      .filter((c) => !picked || (c.days_aged !== null && c.days_aged >= picked.low && (picked.high === null || c.days_aged <= picked.high)))
      .map((c) => fields.map((f) => f.value(c))),
    // `fields` is rebuilt each render from the list; the list and band are what change it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [list, picked],
  );

  const own = term(listTerm);
  const hint = own ? termHint(own) : null;
  const listName = own?.label ?? list.title;
  const title = panelTitle ?? listName;
  const notes = [
    download.problem(block),
    list.total > list.shown
      ? `Showing the oldest ${list.shown.toLocaleString('en-US')} of ${list.total.toLocaleString('en-US')}; the download has every case.`
      : null,
  ].filter(Boolean);
  const footer = notes.length
    ? notes.map((n) => <Box key={n} component="span" sx={{ display: 'block' }}>{n}</Box>)
    : undefined;

  const table = (fill: boolean) => (
    <ResultTable columns={columns} rows={rows} pageSize={25} maxHeight={fill ? undefined : 520} fill={fill} ariaLabel={`${listName} cases`} />
  );

  return (
    <Panel
      title={lists.length > 1 ? title : (
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'baseline', gap: 1 }}>
          {title}
          <Typography component="span" variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>
            {withPremium(list.total, list.total_premium, ' cases')}
          </Typography>
        </Box>
      )}
      subtitle={subtitle}
      info={hint?.title}
      infoTone={hint?.tone}
      tools={
        <>
          {dates.control}
          {lists.length > 1 && (
            <SelectMenu
              label="Status"
              value={block}
              options={lists.map((l) => ({
                value: l.block,
                // Counted on the date this panel is reading, so the status and
                // the list below it cannot show two different totals.
                label: `${dates.source[l.block].terms[l.listTerm]?.label ?? dates.source[l.block].title} · ${withPremium(dates.source[l.block].total, dates.source[l.block].total_premium, ' cases')}`,
              }))}
              onChange={pickStatus}
            />
          )}
          {/* A dropdown, not a row of buttons: this header already carries the
              dates, the status and three buttons, and four age bands with
              their counts took a row of their own. */}
          <SelectMenu
            label="Aged"
            value={band}
            onChange={setBand}
            options={[
              { value: 'all', label: `All · ${withPremium(list.total, list.total_premium, ' cases')}` },
              ...list.aging.map((a) => ({ value: a.key, label: `${a.label} · ${withPremium(a.count, a.premium)}` })),
            ]}
          />
        </>
      }
      actions={
        <>
          {download.button(block, {
            description: `All ${list.total.toLocaleString('en-US')} cases, with the counts by age`,
            tooltip: 'Download every case as Excel',
            disabled: loading || list.total === 0,
            dateBasis: dates.basis,
          })}
        </>
      }
      loading={loading || dates.reloading}
      loadingHeight={360}
      flush
      footer={footer}
      expanded={{ content: table(true) }}
    >
      {table(false)}
    </Panel>
  );
}
